import { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ForumIcon from '@mui/icons-material/Forum'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts'
import {
  collection,
  getCountFromServer,
  query,
  where,
  Timestamp,
} from 'firebase/firestore'
import { db } from '../../config/firebase'
import { useUserStore } from '../../stores/userStore'
import { useSurveyStore } from '../../stores/surveyStore'
import { Survey, SURVEY_DIMENSION_LABELS } from '../../models/Survey'
import { startOfWeek, subWeeks, format } from 'date-fns'

// ── 40 分類計算邏輯 ──────────────────────────────────────────────
const CALLING_DIMS = ['vocation', 'purposeful_work', 'prosocial_motivation', 'life_meaning']
const BFI_DIMS = ['extraversion', 'agreeableness', 'conscientiousness', 'neuroticism', 'openness']

function getDominantKey(scores: Record<string, number>, keys: string[]): string {
  let best = keys[0]
  for (const k of keys) {
    if ((scores[k] ?? 0) > (scores[best] ?? 0)) best = k
  }
  return best
}

function getOutcomeTotal(scores: Record<string, number>): number {
  return Object.values(scores).reduce((sum, v) => sum + v, 0)
}

function calcCategory(
  callingSurvey: Survey,
  outcomeSurvey: Survey,
  bfiSurvey: Survey
): number {
  const outcomeTotal = getOutcomeTotal(outcomeSurvey.scores)
  const outcomeLevel = outcomeTotal >= 25 ? 0 : 1 // 0=高, 1=低
  const callingIdx = CALLING_DIMS.indexOf(getDominantKey(callingSurvey.scores, CALLING_DIMS))
  const bfiIdx = BFI_DIMS.indexOf(getDominantKey(bfiSurvey.scores, BFI_DIMS))
  if (callingIdx < 0 || bfiIdx < 0) return -1
  return outcomeLevel * 20 + callingIdx * 5 + bfiIdx + 1
}

function categoryLabel(num: number): string {
  const idx = num - 1
  const outcomeLevel = idx >= 20 ? '低' : '高'
  const remaining = idx % 20
  const callingIdx = Math.floor(remaining / 5)
  const bfiIdx = remaining % 5
  const callingLabels = SURVEY_DIMENSION_LABELS.career_calling
  const bfiLabels = SURVEY_DIMENSION_LABELS.bfi15
  const callingKey = CALLING_DIMS[callingIdx]
  const bfiKey = BFI_DIMS[bfiIdx]
  return `#${num} ${outcomeLevel}×${callingLabels[callingKey] ?? callingKey}×${bfiLabels[bfiKey] ?? bfiKey}`
}

// ── 統計頁 Stats ────────────────────────────────────────────────
interface Stats {
  totalUsers: number
  activeUsers: number
  totalSurveys: number
  totalDiscussions: number
  totalResources: number
}

interface SurveyCounts {
  career_calling: number
  outcome_expectation: number
  bfi15: number
}

// ── 使用者模式圖表 ────────────────────────────────────────────────
function UserHistoryCharts({ surveys }: { surveys: Survey[] }) {
  const types = ['career_calling', 'outcome_expectation', 'bfi15'] as const
  const typeLabels: Record<string, string> = {
    career_calling: '成人生涯召喚量表',
    outcome_expectation: '結果預期量表',
    bfi15: 'BFI-15 五大人格量表',
  }
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7f7f', '#a4de6c']

  return (
    <Box>
      {types.map((type) => {
        const typeSurveys = surveys
          .filter((s) => s.surveyType === type)
          .sort((a, b) => a.completedAt.getTime() - b.completedAt.getTime())
        if (typeSurveys.length === 0) {
          return (
            <Card key={type} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {typeLabels[type]} — 歷史分數
                </Typography>
                <Typography color="text.secondary">尚無填寫紀錄</Typography>
              </CardContent>
            </Card>
          )
        }
        const dimLabels = SURVEY_DIMENSION_LABELS[type] ?? {}
        const dimKeys = Object.keys(typeSurveys[0].scores)
        const chartData = typeSurveys.map((s) => ({
          date: format(s.completedAt, 'MM/dd HH:mm'),
          ...Object.fromEntries(dimKeys.map((k) => [dimLabels[k] ?? k, s.scores[k] ?? 0])),
        }))
        const dimDisplayKeys = dimKeys.map((k) => dimLabels[k] ?? k)

        return (
          <Card key={type} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {typeLabels[type]} — 歷史分數
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {dimDisplayKeys.map((key, i) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={COLORS[i % COLORS.length]}
                      dot
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

// ── 全體模式圖表 ────────────────────────────────────────────────
function AllUsersCharts({ surveys }: { surveys: Survey[] }) {
  // 取每位用戶各量表的最新一筆
  function latestByUser(type: 'career_calling' | 'outcome_expectation' | 'bfi15') {
    const byUser = new Map<string, Survey>()
    for (const s of surveys) {
      if (s.surveyType !== type) continue
      const existing = byUser.get(s.userId)
      if (!existing || s.completedAt > existing.completedAt) {
        byUser.set(s.userId, s)
      }
    }
    return Array.from(byUser.values())
  }

  // ── 召喚構面分佈
  const callingLatest = latestByUser('career_calling')
  const callingDimLabels = SURVEY_DIMENSION_LABELS.career_calling
  const callingCount = Object.fromEntries(CALLING_DIMS.map((k) => [k, 0]))
  for (const s of callingLatest) {
    const dom = getDominantKey(s.scores, CALLING_DIMS)
    callingCount[dom] = (callingCount[dom] ?? 0) + 1
  }
  const callingData = CALLING_DIMS.map((k) => ({
    name: callingDimLabels[k] ?? k,
    人次: callingCount[k],
  }))

  // ── BFI 構面分佈
  const bfiLatest = latestByUser('bfi15')
  const bfiDimLabels = SURVEY_DIMENSION_LABELS.bfi15
  const bfiCount = Object.fromEntries(BFI_DIMS.map((k) => [k, 0]))
  for (const s of bfiLatest) {
    const dom = getDominantKey(s.scores, BFI_DIMS)
    bfiCount[dom] = (bfiCount[dom] ?? 0) + 1
  }
  const bfiData = BFI_DIMS.map((k) => ({
    name: bfiDimLabels[k] ?? k,
    人次: bfiCount[k],
  }))

  // ── 結果預期高/低
  const outcomeLatest = latestByUser('outcome_expectation')
  let outcomeHigh = 0
  let outcomeLow = 0
  for (const s of outcomeLatest) {
    const total = getOutcomeTotal(s.scores)
    if (total >= 25) outcomeHigh++
    else outcomeLow++
  }
  const outcomeData = [
    { name: '高（≥25）', value: outcomeHigh },
    { name: '低（<25）', value: outcomeLow },
  ]
  const PIE_COLORS = ['#2196F3', '#FF9800']

  // ── 40 分類 Top 10
  const categoryCount = new Map<number, number>()
  const userIds = new Set([
    ...callingLatest.map((s) => s.userId),
    ...outcomeLatest.map((s) => s.userId),
    ...bfiLatest.map((s) => s.userId),
  ])
  const callingMap = new Map(callingLatest.map((s) => [s.userId, s]))
  const outcomeMap = new Map(outcomeLatest.map((s) => [s.userId, s]))
  const bfiMap = new Map(bfiLatest.map((s) => [s.userId, s]))
  for (const uid of userIds) {
    const c = callingMap.get(uid)
    const o = outcomeMap.get(uid)
    const b = bfiMap.get(uid)
    if (!c || !o || !b) continue
    const cat = calcCategory(c, o, b)
    if (cat > 0) categoryCount.set(cat, (categoryCount.get(cat) ?? 0) + 1)
  }
  const top10 = Array.from(categoryCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([num, count]) => ({ name: categoryLabel(num), 人次: count }))
    .reverse() // 水平 bar 低分在上

  // ── 填寫趨勢（近 8 週）
  const now = new Date()
  const weekStarts = Array.from({ length: 8 }, (_, i) =>
    startOfWeek(subWeeks(now, 7 - i), { weekStartsOn: 1 })
  )
  const trendData = weekStarts.map((weekStart, i) => {
    const weekEnd = i < 7 ? weekStarts[i + 1] : now
    const label = format(weekStart, 'MM/dd')
    const count = (type: string) =>
      surveys.filter(
        (s) =>
          s.surveyType === type &&
          s.completedAt >= weekStart &&
          s.completedAt < weekEnd
      ).length
    return {
      week: label,
      成人生涯召喚: count('career_calling'),
      結果預期: count('outcome_expectation'),
      'BFI-15': count('bfi15'),
    }
  })

  return (
    <Box>
      {/* 召喚傾向分佈 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            成人生涯召喚量表 — 召喚傾向分佈
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={callingData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="人次" fill="#2196F3" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* 人格傾向分佈 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            BFI-15 五大人格量表 — 人格傾向分佈
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={bfiData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="人次" fill="#FF9800" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* 結果預期高/低 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            結果預期量表 — 高/低組比例
          </Typography>
          {outcomeLatest.length === 0 ? (
            <Typography color="text.secondary">尚無填寫紀錄</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={outcomeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                >
                  {outcomeData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* 40 分類 Top 10 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            40 分類 Top 10
          </Typography>
          {top10.length === 0 ? (
            <Typography color="text.secondary">需三份量表皆完成才能計算分類</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(top10.length * 36 + 60, 200)}>
              <BarChart
                data={top10}
                layout="vertical"
                margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="人次" fill="#4CAF50" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* 填寫趨勢 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            填寫趨勢（近 8 週）
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="成人生涯召喚" stroke="#2196F3" dot />
              <Line type="monotone" dataKey="結果預期" stroke="#4CAF50" dot />
              <Line type="monotone" dataKey="BFI-15" stroke="#FF9800" dot />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  )
}

// ── 主頁面 ────────────────────────────────────────────────────
export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [surveyCounts, setSurveyCounts] = useState<SurveyCounts | null>(null)
  const [loadingStats, setLoadingStats] = useState(true)

  const { users, fetchUsers } = useUserStore()
  const { allSurveys, loading: loadingSurveys, fetchAllSurveys } = useSurveyStore()

  const [selectedUserId, setSelectedUserId] = useState<string>('all')

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    if (users.length > 0) {
      fetchAllSurveys(users.map((u) => ({ id: u.id, displayName: u.displayName })))
    }
  }, [users.length])

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          totalUsersSnap,
          activeUsersSnap,
          totalSurveysSnap,
          totalDiscussionsSnap,
          totalResourcesSnap,
          careerCallingSnap,
          outcomeExpectationSnap,
          bfi15Snap,
        ] = await Promise.all([
          getCountFromServer(collection(db, 'users')),
          getCountFromServer(
            query(
              collection(db, 'users'),
              where(
                'lastLoginAt',
                '>=',
                Timestamp.fromDate(
                  new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                )
              )
            )
          ),
          getCountFromServer(collection(db, 'surveys')),
          getCountFromServer(collection(db, 'discussions')),
          getCountFromServer(collection(db, 'resources')),
          getCountFromServer(
            query(collection(db, 'surveys'), where('surveyType', '==', 'career_calling'))
          ),
          getCountFromServer(
            query(collection(db, 'surveys'), where('surveyType', '==', 'outcome_expectation'))
          ),
          getCountFromServer(
            query(collection(db, 'surveys'), where('surveyType', '==', 'bfi15'))
          ),
        ])

        setStats({
          totalUsers: totalUsersSnap.data().count,
          activeUsers: activeUsersSnap.data().count,
          totalSurveys: totalSurveysSnap.data().count,
          totalDiscussions: totalDiscussionsSnap.data().count,
          totalResources: totalResourcesSnap.data().count,
        })

        setSurveyCounts({
          career_calling: careerCallingSnap.data().count,
          outcome_expectation: outcomeExpectationSnap.data().count,
          bfi15: bfi15Snap.data().count,
        })
      } catch (error) {
        console.error('Failed to fetch stats:', error)
        setStats({
          totalUsers: 0,
          activeUsers: 0,
          totalSurveys: 0,
          totalDiscussions: 0,
          totalResources: 0,
        })
        setSurveyCounts({ career_calling: 0, outcome_expectation: 0, bfi15: 0 })
      } finally {
        setLoadingStats(false)
      }
    }

    fetchStats()
  }, [])

  // 有填寫紀錄的用戶
  const usersWithSurveys = useMemo(() => {
    const ids = new Set(allSurveys.map((s) => s.userId))
    return users.filter((u) => ids.has(u.id))
  }, [allSurveys, users])

  // 依篩選條件過濾量表
  const filteredSurveys = useMemo(() => {
    if (selectedUserId === 'all') return allSurveys
    return allSurveys.filter((s) => s.userId === selectedUserId)
  }, [allSurveys, selectedUserId])

  if (loadingStats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const statCards = [
    {
      title: '總用戶數',
      value: stats?.totalUsers || 0,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#5C9EAD',
    },
    {
      title: '活躍用戶',
      value: stats?.activeUsers || 0,
      subtitle: '最近 7 天',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
    },
    {
      title: '問卷數量',
      value: stats?.totalSurveys || 0,
      icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
    },
    {
      title: '討論數量',
      value: stats?.totalDiscussions || 0,
      icon: <ForumIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
    },
    {
      title: '資源數量',
      value: stats?.totalResources || 0,
      icon: <LibraryBooksIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
    },
  ]

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        儀表板
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {card.value}
                    </Typography>
                    {card.subtitle && (
                      <Typography variant="caption" color="text.secondary">
                        {card.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: card.color + '15',
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              歡迎使用 NurseBridge 管理後台
            </Typography>
            <Typography variant="body2" color="text.secondary">
              在這裡您可以管理用戶、QR Code、資源和討論區。使用左側選單導覽至不同的管理功能。
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 量表完成統計 */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          量表完成統計
        </Typography>
        <Grid container spacing={3}>
          {[
            {
              label: '成人生涯召喚量表',
              count: surveyCounts?.career_calling ?? 0,
              color: '#2196F3',
            },
            {
              label: '結果預期量表',
              count: surveyCounts?.outcome_expectation ?? 0,
              color: '#4CAF50',
            },
            {
              label: 'BFI-15 五大人格量表',
              count: surveyCounts?.bfi15 ?? 0,
              color: '#FF9800',
            },
          ].map((item) => (
            <Grid item xs={12} sm={4} key={item.label}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.label}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {item.count}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: item.color + '15',
                        color: item.color,
                      }}
                    >
                      <AssignmentIcon sx={{ fontSize: 40 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 量表分析區 */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            量表資料分析
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>篩選用戶</InputLabel>
            <Select
              value={selectedUserId}
              label="篩選用戶"
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <MenuItem value="all">全部使用者</MenuItem>
              {usersWithSurveys.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loadingSurveys ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedUserId === 'all' ? (
          <AllUsersCharts surveys={filteredSurveys} />
        ) : (
          <UserHistoryCharts surveys={filteredSurveys} />
        )}
      </Box>
    </Box>
  )
}
